import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, formatDate } from '../../utils/api'
import { MetricsRow } from '../shared'
import '../../styles/pages.css'

function PIMProducts() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Stats
  const [stats, setStats] = useState({
    brand_count: 0,
    master_product_count: 0,
    variant_count: 0,
    needs_review_count: 0
  })

  // Search - initialize from URL params
  const [brands, setBrands] = useState([])
  const [categories, setCategories] = useState([])
  const [searchFilters, setSearchFilters] = useState({
    model: searchParams.get('model') || '',
    brand_id: searchParams.get('brand_id') || '',
    category_id: searchParams.get('category_id') || ''
  })
  const [products, setProducts] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)

  // Load initial data
  useEffect(() => {
    loadStats()
    loadBrands()
    loadCategories()
    loadProducts()
  }, [])

  async function loadStats() {
    try {
      const response = await api.get('/api/pim/stats')
      setStats(response)
    } catch (err) {
      console.error('Failed to load PIM stats:', err)
    }
  }

  async function loadBrands() {
    try {
      const response = await api.get('/api/pim/brands')
      setBrands(response.brands || [])
    } catch (err) {
      console.error('Failed to load brands:', err)
    }
  }

  async function loadCategories() {
    try {
      const response = await api.get('/api/pim/categories/tree')
      // Flatten tree for dropdown
      const flat = []
      function flatten(cats, depth = 0) {
        for (const cat of cats) {
          flat.push({ ...cat, depth })
          if (cat.children) flatten(cat.children, depth + 1)
        }
      }
      flatten(response.categories || [])
      setCategories(flat)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  const loadProducts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (searchFilters.model) params.set('model', searchFilters.model)
      if (searchFilters.brand_id) params.set('brand_id', searchFilters.brand_id)
      if (searchFilters.category_id) params.set('category_id', searchFilters.category_id)
      params.set('page', page.toString())
      params.set('page_size', pageSize.toString())

      const response = await api.get(`/api/pim/products?${params}`)
      setProducts(response.items || [])
      setTotal(response.total || 0)
    } catch (err) {
      console.error('Failed to load products:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [searchFilters, page, pageSize])

  function handleFilterChange(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  function handleSearch(e) {
    e.preventDefault()
    setPage(1)
    loadProducts()
  }

  function clearFilters() {
    setSearchFilters({ model: '', brand_id: '', category_id: '' })
    setSearchParams({})  // Clear URL params too
    setPage(1)
  }

  function handleRowClick(product) {
    navigate(`/pim/products/${product.master_product_id}`)
  }

  const totalPages = Math.ceil(total / pageSize)

  const metrics = [
    { label: 'Brands', value: stats.brand_count, color: 'blue' },
    { label: 'Master Products', value: stats.master_product_count, color: 'green' },
    { label: 'Variants', value: stats.variant_count, color: 'purple' },
    { label: 'Needs Review', value: stats.needs_review_count, color: stats.needs_review_count > 0 ? 'red' : '' }
  ]

  return (
    <>
      <MetricsRow metrics={metrics} />

      <form onSubmit={handleSearch} style={{ marginTop: 'var(--space-4)' }}>
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <div className="filter-group" style={{ flex: 1, minWidth: 250 }}>
              <label className="filter-label">Model Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by model name..."
                value={searchFilters.model}
                onChange={e => handleFilterChange('model', e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ width: 200 }}>
              <label className="filter-label">Brand</label>
              <select
                className="form-input"
                value={searchFilters.brand_id}
                onChange={e => handleFilterChange('brand_id', e.target.value)}
              >
                <option value="">All Brands</option>
                {brands.map(brand => (
                  <option key={brand.brand_id} value={brand.brand_id}>
                    {brand.name} ({brand.master_product_count})
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group" style={{ width: 250 }}>
              <label className="filter-label">Category</label>
              <select
                className="form-input"
                value={searchFilters.category_id}
                onChange={e => handleFilterChange('category_id', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {'—'.repeat(cat.depth)} {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
            {total > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                {total} product{total !== 1 ? 's' : ''} found
              </span>
            )}
          </div>
        </div>
      </form>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Products Found</div>
            <div className="empty-state-description">Try adjusting your search filters.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Image</th>
                <th>Model</th>
                <th>Brand</th>
                <th>Category</th>
                <th style={{ textAlign: 'center' }}>Variants</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr
                  key={product.master_product_id}
                  onClick={() => handleRowClick(product)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    {product.first_image_url ? (
                      <img
                        src={product.first_image_url}
                        alt={product.model}
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          borderRadius: 4,
                          background: 'var(--color-bg-secondary)'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          background: 'var(--color-bg-tertiary)',
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--color-text-muted)',
                          fontSize: 12
                        }}
                      >
                        No img
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{product.model}</div>
                  </td>
                  <td>{product.brand?.name || '-'}</td>
                  <td className="text-muted">{product.category?.name || 'Uncategorized'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="badge badge-neutral">{product.variant_count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {products.length > 0 && totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--space-3)',
            borderTop: '1px solid var(--color-border)'
          }}>
            <span className="text-sm text-muted">
              Page {page} of {totalPages} ({total} total)
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => { setPage(p => Math.max(1, p - 1)); loadProducts(); }}
                disabled={page === 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); loadProducts(); }}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default PIMProducts
